import { Button, Form, message, Spin, Tabs } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import styles from "../Notice/index.module.css"
import NewModal from "Common/NewModal"
import Step1 from "./Step/Step1"
import Step2 from "./Step/Step2"
import Step3 from "./Step/Step3"
import Step4 from "./Step/Step4"
import {
  actionNotice_item1,
  actionNotice_item3,
  actionNotice_item4,
  actionTaken_item3,
  actionTaken_item4,
  actionTaken_item6,
  actionTaken_item7,
} from "../Notice/Step/options"
import _ from "lodash"
import { fetchDispositionNoticeV2, patchDispositionNoticeV2 } from "./api"
import { getFormatNoticeV2 } from "./format"

export const NoticeV2Context = React.createContext(null)

function initActionNotice(stage, status) {
  if (stage === "Disposed" && (status === "Denied" || status === "PreApprovalRequestedDenied")) {
    return actionNotice_item4
  }
  if (stage === "Disposed" && status === "ClosedForIncompleteness") {
    return actionNotice_item1
  }
  if (
    stage === "Disposed" &&
    (status === "CounterOfferDenied" || status === "ApprovedButNotAccepted" || status === "Withdrawn")
  ) {
    return actionNotice_item3
  }
  return ""
}

function initHmdaActionTaken(stage, status) {
  if (stage === "Disposed" && (status === "Withdrawn" || status === "CounterOfferDenied")) {
    return actionTaken_item3
  }
  if (stage === "Disposed" && status === "Denied") {
    return actionTaken_item6
  }
  if (stage === "Disposed" && status === "ClosedForIncompleteness") {
    return actionTaken_item4
  }
  if (stage === "Disposed" && status === "ApprovedButNotAccepted") {
    return actionTaken_item7
  }
  return ""
}

function prepareFormValues(doc) {
  const l1 = getFormatNoticeV2({ ...(doc.L1 || {}) }, false) || {}
  // Ensure Form.List always receives an array (API/legacy may omit the field).
  if (!Array.isArray(l1.participants)) {
    l1.participants = []
  }
  const result = {
    L1: l1,
    L2: doc.L2 || {},
    L3: getFormatNoticeV2({ ...(doc.L3 || {}) }, false),
    L4: getFormatNoticeV2({ ...(doc.L4 || {}) }, false),
  }
  const stage = doc.currentStage
  const status = doc.currentStatus
  if (!result.L1.actionNotice) {
    result.L1.actionNotice = initActionNotice(stage, status)
  }
  if (!result.L1.hmdaActionTaken) {
    result.L1.hmdaActionTaken = initHmdaActionTaken(stage, status)
  }
  return result
}

export const NoticeV2Modal = ({ setOpen, loanId }) => {
  const [form] = Form.useForm()
  const [activeKey, setActiveKey] = useState("1")
  const [loading, setLoading] = useState(false)
  const [updateLoading, setUpdateLoading] = useState(false)

  const scrollTop = (num) => {
    const modalBody = document.querySelector(".ant-modal-body")
    if (modalBody) modalBody.scrollTop = num || 0
  }

  const loadDetails = async () => {
    setLoading(true)
    try {
      const body = await fetchDispositionNoticeV2(loanId)
      const doc = body.Data || {}
      const values = prepareFormValues(doc)
      // Reset first so Form.List fully rebuilds participant rows from API data.
      form.resetFields()
      form.setFieldsValue(values)
      const step = doc.currentStep
      setActiveKey(`${step && step !== 4 ? step + 1 : "1"}`)
    } catch (err) {
      message.error(err?.message || "Failed to load notice")
    } finally {
      setLoading(false)
    }
  }

  const saveDetails = useCallback(async () => {
    await form.validateFields()
    const dataType = `L${activeKey}`
    const allValues = form.getFieldsValue(true)
    const item = _.cloneDeep(allValues[dataType])
    const data = getFormatNoticeV2(item)
    setUpdateLoading(true)
    try {
      await patchDispositionNoticeV2(loanId, {
        dataType,
        loanId,
        data,
        currentStep: Number(activeKey),
      })
    } catch (err) {
      message.error(err?.message || "Failed to save")
      throw err
    } finally {
      setUpdateLoading(false)
    }
  }, [activeKey, form, loanId])

  useEffect(() => {
    loadDetails()
  }, [loanId])

  const items = [
    { key: "1", label: "Tab 1", forceRender: true, children: <Step1 /> },
    { key: "2", label: "Tab 2", forceRender: true, children: <Step2 /> },
    { key: "3", label: "Tab 3", forceRender: true, children: <Step3 /> },
    { key: "4", label: "Tab 4", forceRender: true, children: <Step4 /> },
  ]

  return (
    <NewModal
      width={976}
      onCancel={() => setOpen(false)}
      onOk={() => {}}
      title="Notice of Action Taken"
      className={[styles.noticeModal, activeKey === "4" && styles.noticeModal_hight].join(" ")}
      centered={true}
      customFooter={null}
      footer={
        activeKey === "4"
          ? [
              <Button key="back" type="link" onClick={() => setActiveKey("3")}>
                Back
              </Button>,
              <Button
                key="done"
                type="primary"
                loading={updateLoading}
                onClick={async () => {
                  try {
                    await saveDetails()
                    setOpen(false)
                  } catch (_) {
                    /* message shown in saveDetails */
                  }
                }}
              >
                Done
              </Button>,
            ]
          : null
      }
    >
      <Spin spinning={loading}>
        <Form form={form} name="noticeForm" labelCol={{ span: 24 }} autoComplete="off">
          <NoticeV2Context.Provider
            value={{
              form,
              setActiveKey,
              saveDetails,
              updateLoading,
              loanId,
              scrollTop,
            }}
          >
            <Tabs
              activeKey={activeKey}
              items={items}
              onChange={(v) => setActiveKey(v)}
              renderTabBar={() => ""}
              className={styles.noticeContent}
            />
          </NoticeV2Context.Provider>
        </Form>
      </Spin>
    </NewModal>
  )
}

const NoticeV2Btn = ({ loanId }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button type="link" onClick={() => setOpen(true)}>
        Notice of Action Taken
      </Button>
      {open && <NoticeV2Modal setOpen={setOpen} loanId={loanId} />}
    </>
  )
}

export default NoticeV2Btn
